import { UDFCompatibleDatafeed } from './udf-compatible-datafeed';

/**
 * Extends UDFCompatibleDatafeed to override getMarks with a client-side cache.
 *
 * Why the cache exists:
 *   TradingView's marks are additive — it never removes entries from its internal
 *   _marks map. When an analyst is deselected we must call clearMarks() to wipe the
 *   cache, but that queues a repaint showing blank marks before the server responds.
 *
 *   By storing marks locally (keyed by symbol+range+resolution+mode, tagged with
 *   analystSlug), we can respond to getMarks() *synchronously* on removal. Because
 *   JavaScript is single-threaded, the repaint queued by clearMarks() only fires
 *   after the current call stack completes — so _marks gets refilled before the
 *   screen ever goes blank.
 *
 * Cache invalidation:
 *   - Symbol change: the datafeed is re-instantiated by TVChartContainer.
 *   - Mode change (aggregateOnBar / direction): different cache key → server fetch.
 *   - Adding a new analyst not in cache: server fetch, cache updated with merged result.
 *   - Removing analysts: pure client-side filter, synchronous, no server round-trip.
 */
export class TraderBroDatafeed extends UDFCompatibleDatafeed {
  constructor(datafeedURL, authorizeToken, overlayStateRef) {
    super(datafeedURL, undefined, authorizeToken);
    this._overlayStateRef = overlayStateRef;
    // Map<cacheKey, { analysts: string[], marks: MarkWithSlug[] }>
    // Marks carry an extra `analystSlug` property that is stripped before passing to TV.
    this._marksCache = new Map();
  }

  _modeKey(state) {
    const analysts = (state.selectedAnalysts || []).slice().sort().join(',');
    const dir = state.direction || 'all';
    const agg = state.aggregateOnBar ? '1' : '0';
    return `${analysts}|${dir}|${agg}`;
  }

  _cacheKey(symbolInfo, from, resolution, state) {
    const dir = state.direction || 'all';
    const agg = state.aggregateOnBar ? '1' : '0';
    // `to` is intentionally excluded: TV's getData() extends it to year 2037 before
    // calling _getData(), but clearAndRefreshMarks() passes the original lastRange.end.
    // Keying only on `from` ensures both paths share the same cache entry.
    return `${symbolInfo.ticker}|${from}|${resolution}|${dir}|${agg}`;
  }

  // Strip analystSlug before handing marks to TradingView (unknown fields cause schema warnings).
  _toTVMarks(marks) {
    return marks.map(({ analystSlug, ...tvMark }) => tvMark);
  }

  getMarks(symbolInfo, from, to, onDataCallback, resolution) {
    if (!this._configuration.supports_marks) return;

    const state = this._overlayStateRef?.current ?? {};

    if (!state.selectedAnalysts?.length) {
      onDataCallback([]);
      return;
    }

    const modeKey = this._modeKey(state);
    const cacheKey = this._cacheKey(symbolInfo, from, resolution, state);
    const cached = this._marksCache.get(cacheKey);
    const selectedSet = new Set(state.selectedAnalysts);

    // Cache hit: all currently selected analysts are covered by the cached response.
    // Respond synchronously so _marks is refilled before TV's pending repaint fires —
    // this eliminates the visible flash when deselecting analysts.
    if (cached && state.selectedAnalysts.every(s => cached.analysts.includes(s))) {
      const filtered = cached.marks.filter(
        m => !m.analystSlug || selectedSet.has(m.analystSlug)
      );
      onDataCallback(this._toTVMarks(filtered));
      return;
    }

    // Cache miss or new analyst added — fetch from server.
    const params = {
      symbol: symbolInfo.ticker || '',
      from,
      to,
      resolution,
      aggregate: 'false',
      show_avatars: 'true',
      analysts: state.selectedAnalysts.join(','),
    };

    if (state.direction && state.direction !== 'all') {
      params.direction = state.direction;
    }

    if (state.aggregateOnBar) {
      params.aggregate_on_bar = 'true';
    }

    this._requester.sendRequest(this._datafeedURL, 'marks', params)
      .then((response) => {
        // Discard if selection/mode changed while the request was in flight.
        const currentKey = this._modeKey(this._overlayStateRef?.current ?? {});
        if (modeKey !== currentKey) return;

        const result = [];
        if (!Array.isArray(response)) {
          for (let i = 0; i < (response.id?.length ?? 0); i++) {
            const mark = {
              id: response.id[i],
              time: response.time[i],
              color: response.color[i],
              labelFontColor: response.labelFontColor?.[i] ?? '#ffffff',
              text: response.text[i],
              label: response.label[i],
              minSize: response.minSize[i],
              analystSlug: response.analystSlug?.[i] ?? null,
            };
            if (response.imageUrl?.[i]) mark.imageUrl = response.imageUrl[i];
            result.push(mark);
          }
        } else {
          response.forEach(m => result.push(m));
        }

        // Merge into cache so subsequent subset-removals can be served without a server round-trip.
        const cacheKey = this._cacheKey(symbolInfo, from, resolution, state);
        const existing = this._marksCache.get(cacheKey);
        const mergedMarks = existing ? [...existing.marks] : [];
        const mergedIds = new Set(mergedMarks.map(m => m.id));
        result.forEach(m => {
          if (mergedIds.has(m.id)) {
            mergedMarks[mergedMarks.findIndex(x => x.id === m.id)] = m;
          } else {
            mergedMarks.push(m);
          }
        });
        const mergedAnalysts = Array.from(
          new Set([...(existing?.analysts ?? []), ...state.selectedAnalysts])
        );
        this._marksCache.set(cacheKey, { analysts: mergedAnalysts, marks: mergedMarks });

        onDataCallback(this._toTVMarks(result));
      })
      .catch(() => onDataCallback([]));
  }
}
